import $ from 'jquery';
import global_table_layout_methods from './global_table_layout_methods';

function build_column_id(header) {
  return String(header || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "column";
}

function build_column_meta(header) {
  const meta = {
    id: build_column_id(header),
    label: String(header || "").trim(),
  };
  return meta;
}

export default {
  get_resolved_row_id(row) {
    return row?.__row_id ?? row?.id ?? null;
  },
  get_row_key(row, index) {
    const row_id = this.get_resolved_row_id(row);
    return row_id !== null ? `row-${row_id}` : `row-index-${index}`;
  },

  get_row_value(row, header) {
    if (!row || !header) {
      return null;
    }
    return Reflect.get(row, header);
  },

  get_column_id(header) {
    return build_column_meta(header).id;
  },

  get_column_meta: build_column_meta,

  is_toggleable_column(header) {
    return String(header || "").trim().toLowerCase() !== "no";
  },

  is_column_hidden(column_id) {
    return this.hidden_column_ids.includes(column_id);
  },
  can_hide_column(column_id) {
    return !!(
      this.enable_column_toggle &&
      column_id &&
      !this.is_column_hidden(column_id) &&
      this.get_visible_toggleable_column_count() > 1
    );
  },

  get_visible_toggleable_column_count() {
    return (this.headers || []).filter((header) => {
      if (!this.is_toggleable_column(header)) {
        return false;
      }
      const column_id = this.get_column_id(header);
      return !this.is_column_hidden(column_id);
    }).length;
  },

  get_visible_table_column_count() {
    const table_element = this.get_table_element();
    if (!table_element) {
      return 0;
    }

    const header_cells = Array.from(table_element.querySelectorAll("thead th"));
    return header_cells.filter((cell) => window.getComputedStyle(cell).display !== "none").length;
  },

  get_hidden_columns() {
    return (this.headers || [])
      .map((header) => this.get_column_meta(header))
      .filter((column) => this.is_column_hidden(column.id));
  },

  format_row_value(value) {
    return value || value === 0 ? value : '-';
  },

  split_cell_value(value) {
    const formatted = this.format_row_value(value);
    if (formatted === "-") {
      const empty_parts = { primary: "-", details: "" };
      return empty_parts;
    }
    return this.build_split_cell_parts(formatted);
  },
  build_split_cell_parts(formatted) {
    const parts = String(formatted)
      .replace(/\r/g, "")
      .split("\n")
      .map((part) => part.trim())
      .filter(Boolean);
    const split_parts = { primary: parts[0] || "-", details: parts.slice(1).join("\n") };
    return split_parts;
  },

  normalize_indicator_value(value) {
    if (value === null || typeof value === "undefined") {
      return "";
    }
    return String(value).trim().toLowerCase();
  },

  is_positive_indicator(value) {
    return ["y", "yes"].includes(this.normalize_indicator_value(this.split_cell_value(value).primary));
  },

  is_negative_indicator(value) {
    return ["n", "no"].includes(this.normalize_indicator_value(this.split_cell_value(value).primary));
  },

  is_boolean_indicator(value) {
    return this.is_positive_indicator(value) || this.is_negative_indicator(value);
  },

  get_indicator_badge_class(value) {
    return this.is_positive_indicator(value)
      ? "global-table-indicator global-table-indicator--yes"
      : "global-table-indicator global-table-indicator--no";
  },

  ...global_table_layout_methods,

  initialize_data_table() {
    this.is_initializing = true;
    this.$nextTick(() => {
      const table_id = `#${this.table_id}`;
      const table_element = $(table_id);

      if (!table_element.length) {
        this.is_initializing = false;
        return;
      }

      this.destroy_data_table();

      if (this.data && this.data.length > 0) {
        table_element.DataTable({
          autoWidth: true,
          responsive: this.responsive,
          ordering: false,
          drawCallback: () => {
            this.apply_hidden_columns();
            this.bind_header_toggle_events();
            this.update_table_fit_state();
          },
          language: {
            info: "",
            lengthMenu: "_MENU_&nbsp;&nbsp; entries per page",
            emptyTable: ""
          }
        });
      }
      this.apply_hidden_columns();
      this.bind_header_toggle_events();
      this.update_table_fit_state();
      this.is_initializing = false;
    });
  },

  get_cell_class(key) {
    const normalized_key = String(key || "").toLowerCase();
    return ["qualification", "experience", "results of preliminary"].some((value) => normalized_key.includes(value))
      ? "global-table-cell-long"
      : "";
  },

  get_cell_title(value) {
    if (value === null || typeof value === "undefined") {
      return "";
    }
    return String(value);
  },

  get_cell_primary_value(value) {
    return this.split_cell_value(value).primary;
  },

  get_cell_details_value(value) {
    return this.split_cell_value(value).details;
  }
};
